import { Fragment, useMemo, useState } from 'react';
import { useSaSorgu } from './useSatisVerisi';
import { SectionCard, fmtTRY } from '../yeni-design/primitives';
import type { Donem } from './donem';
import type { SaKaynak } from './ekranKaynaklari';
import { kanalRengi } from './kanalRenk';
import { kanalGrubu } from './kanal';
import { oranMetni } from './bicim';
import { subeSuz } from './subeSuzgeci';
import { BlokIskeleti } from './BlokIskeleti';
import { DeltaHucre } from './DeltaHucre';
import { sayi, yuzdeDegisim, type KanalSatiri } from './ozetHesaplari';

/** Iskeletin govde yuksekligi: yerini tuttugu tablo veri odakli, en genis halinde pay cubugu + 3 grup +
 * E-Ticaret'in 4 alt kanali (Trendyol, Yemeksepeti, Kendi Online, Getir) ile doluyor, o yuzden 200px
 * degeri kucultulmez (CLS, 2026-08-29 dersi). */
export const KANAL_ISKELET_YUKSEKLIGI = 200;

/** Spec §7.2: E-Ticaret acik baslar; alt kanallari ilk bakista gorunur. */
export const ACIK_BASLAYAN_GRUP = 'E-Ticaret';

const ALT_BASLIK = 'Grubu açınca alt kanallar · Δ önceki döneme göre';

export interface KanalOzet {
  kanal: string;
  ciro: number;
  pay: number;
  delta: number | null;
}

export interface GrupOzet {
  grup: string;
  ciro: number;
  fis: number;
  pay: number;
  delta: number | null;
  /** Yalniz Mağaza (spec §7.2): kurumsal/yemek karti ve pazaryeri fisleri sepet anlaminda karsilastirilamaz. */
  sepet: number | null;
  kanallar: KanalOzet[];
}

/**
 * 990040 satirlari -> grup/alt kanal tablosu. Pay TOPLAM ciro uzerinden (alt kanal payi da). Δ onceki pencere
 * satirlarindan; `onceki` null (henuz gelmedi) ya da grupta onceki ciro 0/yok ise null ("—", sahte %∞ yok).
 * Grup adiyla ayni adli kanal (Mağaza) alt satir olmaz.
 */
export function kanalTablosu(bu: KanalSatiri[], onceki: KanalSatiri[] | null): GrupOzet[] {
  const toplam = bu.reduce((t, r) => t + sayi(r.net_ciro), 0);
  const oncekiGrup = new Map<string, number>();
  const oncekiKanal = new Map<string, number>();
  for (const r of onceki ?? []) {
    const g = String(r.kanal_grup);
    const k = String(r.kanal);
    oncekiGrup.set(g, (oncekiGrup.get(g) ?? 0) + sayi(r.net_ciro));
    oncekiKanal.set(k, (oncekiKanal.get(k) ?? 0) + sayi(r.net_ciro));
  }
  const delta = (ciro: number, harita: Map<string, number>, ad: string) =>
    onceki === null ? null : yuzdeDegisim(ciro, harita.get(ad) ?? null);

  const grupMap = new Map<string, { ciro: number; fis: number; kanallar: Map<string, number> }>();
  for (const r of bu) {
    const g = String(r.kanal_grup);
    const k = String(r.kanal);
    const v = grupMap.get(g) ?? { ciro: 0, fis: 0, kanallar: new Map<string, number>() };
    v.ciro += sayi(r.net_ciro);
    v.fis += sayi(r.fis_sayisi);
    v.kanallar.set(k, (v.kanallar.get(k) ?? 0) + sayi(r.net_ciro));
    grupMap.set(g, v);
  }

  return Array.from(grupMap.entries())
    .map(([grup, v]) => ({
      grup,
      ciro: v.ciro,
      fis: v.fis,
      pay: toplam > 0 ? (v.ciro / toplam) * 100 : 0,
      delta: delta(v.ciro, oncekiGrup, grup),
      sepet: grup === 'Mağaza' && v.fis > 0 ? v.ciro / v.fis : null,
      kanallar: Array.from(v.kanallar.entries())
        .filter(([kanal]) => kanal !== grup)
        .map(([kanal, ciro]) => ({
          kanal,
          ciro,
          pay: toplam > 0 ? (ciro / toplam) * 100 : 0,
          delta: delta(ciro, oncekiKanal, kanal),
        }))
        .sort((a, b) => b.ciro - a.ciro),
    }))
    .sort((a, b) => b.ciro - a.ciro);
}

interface Props {
  /** 990040 (sayfa cozer: ekran once, menu yedegi). */
  kaynak: SaKaynak | undefined;
  donem: Donem;
  /** Δ kolonu: 990040'in onceki pencere cagrisi — OzetSerit ile AYNI anahtar, ek istek yok. */
  oncekiDonem: Donem;
  /** null = tum subeler (istemcide suzulur). */
  sube?: string | null;
  /** G2: secili kanal VURGULANIR; blok kendini suzmez (paylar tum kanallar uzerinden gorunur kalir). */
  kanal?: string | null;
  /** Verilirse grup ve alt kanal adi detay dugmesi olur (C-modal: M2 Kanal). */
  onKanalSec?: (kanal: string) => void;
}

function KanalAdi({ ad, onKanalSec, kalin }: { ad: string; onKanalSec?: (kanal: string) => void; kalin: boolean }) {
  const sinif = `truncate text-left ${kalin ? 'font-medium text-ink-900' : 'text-ink-700'}`;
  if (!onKanalSec) return <span className={sinif}>{ad}</span>;
  return (
    <button
      type="button"
      data-testid={`kanal-detay-${ad}`}
      onClick={() => onKanalSec(ad)}
      title={`${ad} ayrıntısı`}
      className={`focus-ring hover:underline ${sinif}`}
    >
      {ad}
    </button>
  );
}

/**
 * Satir 2 sag — Satis kanali tablosu (G2b/K4). Ustte pay cubugu; tablo `Kanal · Ciro · Pay · Δ · Sepet`; grup
 * satiri acilinca alt kanallar. Sorgu `sube: null, kanal: null` ile kurulur: 040 anahtari
 * OzetSerit/SubeBlok/ZamanBlok/useSubeler ile ayni (spec §4.4, ilk acilis 7 istek).
 */
export function KanalBlok({ kaynak, donem, oncekiDonem, sube = null, kanal = null, onKanalSec }: Props) {
  const [acik, setAcik] = useState<ReadonlySet<string>>(() => new Set([ACIK_BASLAYAN_GRUP]));
  const buQ = useSaSorgu<KanalSatiri>(kaynak, { donem, sube: null, kanal: null });
  const oncekiQ = useSaSorgu<KanalSatiri>(kaynak, { donem: oncekiDonem, sube: null, kanal: null });

  const gruplar = useMemo(
    () => kanalTablosu(subeSuz(buQ.data?.data ?? [], sube), oncekiQ.data ? subeSuz(oncekiQ.data.data, sube) : null),
    [buQ.data, oncekiQ.data, sube],
  );

  const seciliGrup = kanal === null ? null : kanalGrubu(kanal) ?? null;
  const degistir = (grup: string) =>
    setAcik((s) => {
      const y = new Set(s);
      if (y.has(grup)) y.delete(grup);
      else y.add(grup);
      return y;
    });

  if (!kaynak) return null; // kaynak yok -> sorgu bile acilmaz (enabled=false)
  // Sorgu SURERKEN blok `null` DONMEZ: yerini tutan iskelet (bkz. BlokIskeleti olcumu).
  if (buQ.isLoading)
    return (
      <BlokIskeleti
        testid="blok-kanal-iskelet"
        baslik="Satış kanalı"
        altBaslik={ALT_BASLIK}
        yukseklik={KANAL_ISKELET_YUKSEKLIGI}
      />
    );
  if (gruplar.length < 2) return null; // bos durum kurali: tek kanal grubu -> blok gizlenir

  return (
    <div data-testid="blok-kanal">
      <SectionCard title="Satış kanalı" subtitle={ALT_BASLIK}>
        <div
          data-testid="kanal-pay-cubugu"
          aria-hidden="true"
          className="flex h-2.5 rounded-[3px] overflow-hidden bg-ink-100 mb-3"
        >
          {gruplar
            .filter((g) => g.pay > 0)
            .map((g) => (
              <span
                key={g.grup}
                data-testid={`kanal-pay-${g.grup}`}
                className="block h-full"
                style={{ width: `${g.pay}%`, background: kanalRengi(g.grup) }}
              />
            ))}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px] border-collapse">
            <thead>
              <tr className="text-[12px] text-ink-500 border-b border-border">
                <th className="text-left font-medium py-2 pr-3">Kanal</th>
                <th className="text-right font-medium py-2 px-3">Ciro</th>
                <th className="text-right font-medium py-2 px-3">Pay</th>
                <th className="text-right font-medium py-2 px-3">Δ</th>
                <th className="text-right font-medium py-2 pl-3">Sepet</th>
              </tr>
            </thead>
            <tbody>
              {gruplar.map((g) => {
                const altVar = g.kanallar.length > 0;
                // Tek kanal seciliyse grubu kendiliginden acik gelir (kullanici o kanali gorsun).
                const otomatikAcik = kanal !== null && kanal !== g.grup && g.grup === seciliGrup;
                const grupAcik = altVar && (acik.has(g.grup) || otomatikAcik);
                const soluk = seciliGrup !== null && g.grup !== seciliGrup;
                return (
                  <Fragment key={g.grup}>
                    <tr
                      data-testid={`kanal-satir-${g.grup}`}
                      data-secili={g.grup === seciliGrup ? 'true' : undefined}
                      className={`border-b border-border ${soluk ? 'opacity-40' : ''}`}
                    >
                      <td className="py-2 pr-3">
                        <span className="flex items-center gap-2 min-w-0">
                          {altVar ? (
                            <button
                              type="button"
                              data-testid={`kanal-genislet-${g.grup}`}
                              aria-expanded={grupAcik}
                              aria-label={`${g.grup} alt kanalları`}
                              onClick={() => degistir(g.grup)}
                              className="focus-ring w-4 shrink-0 text-[10px] text-ink-400"
                            >
                              {grupAcik ? '▼' : '▶'}
                            </button>
                          ) : (
                            <span className="w-4 shrink-0" aria-hidden="true" />
                          )}
                          <span
                            className="w-2 h-2 rounded-[2px] shrink-0"
                            aria-hidden="true"
                            style={{ background: kanalRengi(g.grup) }}
                          />
                          <KanalAdi ad={g.grup} onKanalSec={onKanalSec} kalin />
                        </span>
                      </td>
                      <td className="py-2 px-3 text-right tnum text-ink-900 whitespace-nowrap">{fmtTRY(g.ciro, { decimals: 0 })}</td>
                      <td className="py-2 px-3 text-right tnum text-ink-500">{oranMetni(g.pay, 1)}</td>
                      <td className="py-2 px-3 text-right whitespace-nowrap"><DeltaHucre deger={g.delta} /></td>
                      <td className="py-2 pl-3 text-right tnum text-ink-500 whitespace-nowrap">
                        {g.sepet !== null ? fmtTRY(g.sepet) : '—'}
                      </td>
                    </tr>
                    {grupAcik &&
                      g.kanallar.map((k) => (
                        <tr
                          key={k.kanal}
                          data-testid={`kanal-satir-${k.kanal}`}
                          data-secili={k.kanal === kanal ? 'true' : undefined}
                          className={`border-b border-border bg-page text-[12.5px] ${
                            soluk || (otomatikAcik && k.kanal !== kanal) ? 'opacity-60' : ''
                          }`}
                        >
                          <td className="py-1.5 pr-3 pl-8">
                            <KanalAdi ad={k.kanal} onKanalSec={onKanalSec} kalin={k.kanal === kanal} />
                          </td>
                          <td className="py-1.5 px-3 text-right tnum text-ink-700 whitespace-nowrap">{fmtTRY(k.ciro, { decimals: 0 })}</td>
                          <td className="py-1.5 px-3 text-right tnum text-ink-500">{oranMetni(k.pay, 1)}</td>
                          <td className="py-1.5 px-3 text-right whitespace-nowrap"><DeltaHucre deger={k.delta} /></td>
                          <td className="py-1.5 pl-3 text-right tnum text-ink-400">—</td>
                        </tr>
                      ))}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
